import {
    Checkbox,
    CircularProgress,
    FormControl,
    FormControlLabel,
    Radio,
    RadioGroup,
    Typography
} from '@mui/material';
import { red } from '@mui/material/colors';
import { action, computed, makeObservable, observable, runInAction } from 'mobx';
import { inject, observer } from 'mobx-react';

import ReactComponent from '../../../ReactComponent';
import ModalConfirmacao from '../../../sessao/ModalConfirmacao';

class PresidenteSignatario extends ReactComponent {
    salvando = false;
    mostrarModalConfirmacao = false;
    comportamentoAssinaturaPresidente = 'apenas_marcar';
    marcadoPendente = false;

    setComportamentoAssinatura = (event) => {
        this.comportamentoAssinaturaPresidente = event.target.value;
    };

    handleCancelarModal = () => {
        this.mostrarModalConfirmacao = false;
    };

    handleConfirmacaoModal = () => {
        this.mostrarModalConfirmacao = false;

        const isInserirAutotexto = this.marcadoPendente
            ? this.comportamentoAssinaturaPresidente === 'inserir_autotexto'
            : true;

        this.executarAlteracaoPresidente(this.marcadoPendente, isInserirAutotexto);
    };

    togglePresidente = async (event) => {
        if (this.salvando) {
            return;
        }

        const marcado = event.target.checked;

        if (this.registro?.certidaoJulgamentoAssinada) {
            if (marcado) {
                const jaPresente = this.presidente ? await this.autotextoJaPresente(this.presidente) : false;

                if (jaPresente) {
                    this.mensagemStore.abrirMensagemAlerta('Observação já presente no texto.');
                    this.executarAlteracaoPresidente(marcado, false);
                    return;
                }

                runInAction(() => {
                    this.marcadoPendente = true;
                    this.comportamentoAssinaturaPresidente = 'apenas_marcar';
                    this.mostrarModalConfirmacao = true;
                });
            } else {
                runInAction(() => {
                    this.marcadoPendente = false;
                    this.mostrarModalConfirmacao = true;
                });
            }
        } else {
            this.executarAlteracaoPresidente(marcado, true);
        }
    };

    executarAlteracaoPresidente = async (marcado, isInserirAutotexto) => {
        this.salvando = true;

        try {
            await this.processarAlteracaoPresidente(marcado ? this.presidente : null, isInserirAutotexto);
        } catch (error) {
            console.error('Erro ao processar alteração de presidente signatário:', error);
            this.mensagemStore.abrirMensagemAlerta(
                'Não foi possível salvar o presidente signatário. O documento pode ter sido alterado; verifique antes de continuar.'
            );
        } finally {
            runInAction(() => {
                this.salvando = false;
            });
        }
    };

    constructor(props) {
        super(props);

        makeObservable(this, {
            salvando: observable,
            mostrarModalConfirmacao: observable,
            comportamentoAssinaturaPresidente: observable,
            marcadoPendente: observable,
            registro: computed,
            presidente: computed,
            presidenteExibicao: computed,
            siglaPresidenteSalva: computed,
            checked: computed,
            tooltipPresidente: computed,
            setComportamentoAssinatura: action,
            handleCancelarModal: action,
            handleConfirmacaoModal: action,
            togglePresidente: action,
            processarAlteracaoPresidente: false,
            aplicarAlteracaoPresidente: false,
            prepararTextosSignatario: false,
            salvarEAtualizarTextosSignatario: false,
            executarAlteracaoPresidente: false,
            montarCargoSignatario: false,
            gerarTratamento: false,
            montarTextoAutotexto: false,
            autotextoJaPresente: false,
            obterDecisaoAtual: false,
            adicionarObservacao: false,
            removerObservacao: false,
            removerAlteracaoPresidente: false,
            renderModalConfirmacao: false,
            render: false
        });
    }

    get registro() {
        return this.observableProps.registro;
    }

    get presidente() {
        const composicao = this.sessaoStore.composicaoPadraoOJC.find((m) => m.presidenteSessao === true);
        return composicao ? composicao.titular : null;
    }

    get presidenteExibicao() {
        if (!this.siglaPresidenteSalva) {
            return null;
        }
        return this.dominioStore.magistrados?.find((m) => m.sigla === this.siglaPresidenteSalva) || null;
    }

    get siglaPresidenteSalva() {
        return this.registro.informacoesAdicionais?.siglaPresidente;
    }

    get checked() {
        return !!this.siglaPresidenteSalva;
    }

    get tooltipPresidente() {
        if (!this.siglaPresidenteSalva) {
            return '';
        }
        if (!this.presidenteExibicao) {
            return 'Presidente ' + this.siglaPresidenteSalva + ' assinará o acórdão';
        }
        return (
            'Presidente ' + this.presidenteExibicao.nome + ' (' + this.presidenteExibicao.sigla + ') assinará o acórdão'
        );
    }

    async processarAlteracaoPresidente(magistrado, isInserirAutotexto) {
        if (magistrado) {
            await this.aplicarAlteracaoPresidente(magistrado, isInserirAutotexto);
        } else {
            await this.removerAlteracaoPresidente();
        }

        runInAction(() => {
            this.registro.presidenteSessaoJulgamento = magistrado;

            if (this.registro.informacoesAdicionais) {
                this.registro.informacoesAdicionais.siglaPresidente = magistrado ? magistrado.sigla : null;
            }
        });
    }

    montarCargoSignatario(magistrado) {
        const descricaoCargo = this.dominioStore.getDescricaoCargo(
            magistrado.codigoCargo,
            magistrado.genero
        ).descricaoCargo;
        const descricaoOrgaoJudicante = this.sessaoStore.sessaoSelecionada.orgaoJudicante.descricao;

        const prefixos = ['Tribunal', 'Órgão', 'Orgao', 'Pleno', 'Conselho', 'Gabinete'];
        const isMasculino = prefixos.some((prefixo) => descricaoOrgaoJudicante.trim().startsWith(prefixo));
        const artigo = isMasculino ? 'o ' : 'a ';

        return descricaoCargo + ' Presidente d' + artigo + descricaoOrgaoJudicante;
    }

    gerarTratamento(magistrado) {
        return this.salaSessaoStore.pronomeTratamentoComCargo(magistrado);
    }

    montarTextoAutotexto(magistrado) {
        return (
            this.gerarTratamento(magistrado) +
            ' ' +
            magistrado.nome.trim() +
            ', Presidente, assinará o acórdão nos termos do art. 165, parágrafo único, do Regimento Interno do Tribunal Superior do Trabalho'
        );
    }

    async obterDecisaoAtual() {
        if (this.observableProps.textoAtual !== undefined) {
            return this.observableProps.textoAtual;
        }

        if (this.registro.certidaoGerada) {
            const sessao = this.sessaoStore.sessaoSelecionada;
            const certidaoJSON = await this.certidaoService.recuperarCamposCertidao(
                this.registro,
                sessao.orgaoJudicante.sigla,
                sessao.orgaoJudicante.descricao,
                sessao.tipoJulgamento
            );
            return certidaoJSON?.decisao || '';
        }

        return this.registro.decisao || '';
    }

    async autotextoJaPresente(magistrado) {
        const textoAutotexto = this.montarTextoAutotexto(magistrado);

        if (this.observableProps.textoAtual !== undefined && this.observableProps.textoAtual.includes(textoAutotexto))
            return true;

        const decisaoAtual = await this.obterDecisaoAtual();

        if (this.registro.certidaoGerada) {
            return decisaoAtual.includes(textoAutotexto);
        }

        return (this.registro.rascunhoCertidao || '').includes(textoAutotexto);
    }

    adicionarObservacao(textoBase, conteudo) {
        const texto = textoBase || '';

        if (texto.includes(conteudo)) {
            return texto;
        }

        const textoRenumerado = this.salaSessaoStore.renumerarObservacoes(texto);
        const numeroObservacao = this.salaSessaoStore.getQuantidadeObservacoes(texto) + 1;

        return textoRenumerado + '\nObservação ' + numeroObservacao + ': ' + conteudo + '.\n';
    }

    removerObservacao(textoBase, conteudo) {
        if (!textoBase || !textoBase.includes(conteudo)) {
            return textoBase;
        }

        const regexTodasObservacoes = /\n?Observação \d+: [\s\S]*?(?=\nObservação \d+:|$)/g;

        let novoTexto = textoBase.replaceAll(regexTodasObservacoes, (match) => {
            if (match.includes(conteudo)) {
                return '';
            }
            return match;
        });

        if (novoTexto.includes(conteudo)) {
            novoTexto = novoTexto.replace(conteudo, '');
        }

        return this.salaSessaoStore.renumerarObservacoes(novoTexto);
    }

    async prepararTextosSignatario(magistrado) {
        const textoAutotexto = this.montarTextoAutotexto(magistrado);

        const rascunhoAntigo = this.registro.rascunhoCertidao || '';
        const novoRascunho = this.adicionarObservacao(rascunhoAntigo, textoAutotexto);

        const decisaoAntiga = await this.obterDecisaoAtual();
        const novaDecisao = this.adicionarObservacao(decisaoAntiga, textoAutotexto);

        return {
            rascunhoAntigo,
            decisaoAntiga,
            novoRascunho,
            novaDecisao,
            isRascunhoMudou: novoRascunho !== rascunhoAntigo,
            isDecisaoMudou: novaDecisao !== decisaoAntiga
        };
    }

    async removerAlteracaoPresidente() {
        if (!this.presidente) {
            await this.certidaoService.removerPresidente(this.registro);
            return;
        }

        const textoAutotexto = this.montarTextoAutotexto(this.presidente);
        const rascunhoAntigo = this.registro.rascunhoCertidao || '';
        const novoRascunho = this.removerObservacao(rascunhoAntigo, textoAutotexto);

        const decisaoAntiga = await this.obterDecisaoAtual();
        const novaDecisao = this.removerObservacao(decisaoAntiga, textoAutotexto);

        const isRascunhoMudou = novoRascunho !== rascunhoAntigo;
        const isDecisaoMudou = novaDecisao !== decisaoAntiga;

        try {
            await this.certidaoService.removerPresidente(this.registro);

            if (isRascunhoMudou || isDecisaoMudou) {
                if (this.registro.certidaoJulgamentoAssinada) {
                    runInAction(() => (this.registro.certidaoJulgamentoAssinada = false));
                }
                if (this.props.callbackDesbloqueio) {
                    this.props.callbackDesbloqueio();
                }
            }

            await this.salvarEAtualizarTextosSignatario(novoRascunho, novaDecisao, isRascunhoMudou, isDecisaoMudou);
        } catch (error) {
            await this.salvarEAtualizarTextosSignatario(rascunhoAntigo, decisaoAntiga, isRascunhoMudou, isDecisaoMudou);
            throw error;
        }
    }

    async salvarEAtualizarTextosSignatario(rascunho, decisao, alterarRascunho, alterarDecisao) {
        if (alterarRascunho) {
            await this.salaSessaoStore.salvarRascunhoCertidao(this.registro, rascunho);
            runInAction(() => (this.registro.rascunhoCertidao = rascunho));
        }

        if (alterarDecisao && this.registro.certidaoGerada) {
            await this.certidaoService.salvarDecisao(this.registro, decisao);

            if (this.props.onChangeTexto) {
                this.props.onChangeTexto(decisao);
            }
        }
    }

    async aplicarAlteracaoPresidente(magistrado, isInserirAutotexto) {
        const cargoSignatario = this.montarCargoSignatario(magistrado);

        runInAction(() => {
            this.registro.rascunhoEmEdicao = true;
        });

        if (!isInserirAutotexto) {
            await this.certidaoService.salvarPresidente(this.registro, magistrado, cargoSignatario);
            return;
        }

        const textos = await this.prepararTextosSignatario(magistrado);

        if (!textos.isRascunhoMudou && !textos.isDecisaoMudou) {
            this.mensagemStore.abrirMensagemAlerta('Observação já presente no texto.');
            await this.certidaoService.salvarPresidente(this.registro, magistrado, cargoSignatario);
            return;
        }

        try {
            await this.certidaoService.salvarPresidente(this.registro, magistrado, cargoSignatario);

            await this.salvarEAtualizarTextosSignatario(
                textos.novoRascunho,
                textos.novaDecisao,
                textos.isRascunhoMudou,
                textos.isDecisaoMudou
            );

            if (this.registro.certidaoJulgamentoAssinada) {
                runInAction(() => (this.registro.certidaoJulgamentoAssinada = false));
            }
            if (this.props.callbackDesbloqueio) {
                this.props.callbackDesbloqueio();
            }
        } catch (error) {
            await this.salvarEAtualizarTextosSignatario(
                textos.rascunhoAntigo,
                textos.decisaoAntiga,
                textos.isRascunhoMudou,
                textos.isDecisaoMudou
            );
            throw error;
        }
    }

    renderModalConfirmacao() {
        if (!this.marcadoPendente) {
            return (
                <ModalConfirmacao
                    callbackCancelar={this.handleCancelarModal}
                    callbackConfirmar={this.handleConfirmacaoModal}
                    open={this.mostrarModalConfirmacao}
                    titulo="Desbloquear assinatura?"
                >
                    <Typography>Esta alteração vai desbloquear a assinatura. Confirma?</Typography>
                </ModalConfirmacao>
            );
        }

        return (
            <ModalConfirmacao
                callbackCancelar={this.handleCancelarModal}
                callbackConfirmar={this.handleConfirmacaoModal}
                open={this.mostrarModalConfirmacao}
                textoBotaoCancelamento="Cancelar"
                textoBotaoConfirmacao="Confirmar"
                titulo="ATENÇÃO"
            >
                <Typography display={'inline'}>A certidão já está assinada. Escolha como deseja prosseguir.</Typography>
                <FormControl>
                    <RadioGroup
                        aria-labelledby="gerar-autotexto-ou-nao"
                        name="comportamento-autotexto-group"
                        onChange={this.setComportamentoAssinatura}
                        value={this.comportamentoAssinaturaPresidente}
                    >
                        <FormControlLabel
                            control={<Radio />}
                            label="Apenas marcar a opção para viabilizar a assinatura, sem alterar o conteúdo da certidão."
                            value="apenas_marcar"
                        />
                        <FormControlLabel
                            control={<Radio />}
                            label="Marcar a opção para viabilizar a assinatura e inserir o autotexto referente à assinatura do acórdão (requer nova assinatura da certidão)."
                            value="inserir_autotexto"
                        />
                    </RadioGroup>
                </FormControl>
            </ModalConfirmacao>
        );
    }

    render() {
        return (
            <>
                <div
                    style={{ display: 'flex', alignItems: 'center', fontSize: '0.95rem' }}
                    title={this.tooltipPresidente}
                >
                    {this.salvando ? (
                        <CircularProgress
                            size={20}
                            style={{ margin: '2px 10px' }}
                        />
                    ) : (
                        <Checkbox
                            checked={this.checked}
                            disabled={!this.presidente}
                            onChange={this.togglePresidente}
                            size="small"
                            style={{ padding: '2px 10px' }}
                        />
                    )}
                    <Typography
                        style={{
                            color: this.checked ? red[800] : '#01579b',
                            fontWeight: 'bold',
                            fontSize: '0.95rem'
                        }}
                    >
                        {'Presidente ' +
                            (this.siglaPresidenteSalva ? this.siglaPresidenteSalva + ' ' : '') +
                            'assinará o acórdão'}
                    </Typography>
                </div>
                {this.renderModalConfirmacao()}
            </>
        );
    }
}

export default inject('stores')(observer(PresidenteSignatario));
